<template>
    <div>
        <div class="grid grid-cols-12 gap-6">
            <div class="hidden md:col-span-1 md:block" />
            <div class="col-span-12 text-left md:col-span-10">
                <h2>Historie ulic 1900–1989</h2>
                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/historie_ulic_1900_1989.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Proslulé kočičí hlavy reprezentují povrch pražských
                        ulic první poloviny 20. století. / Zdroj: Marek
                        Lašťovka</span
                    >
                </div>
                <p class="mb-5 font-bold">
                    Vývoj pražského uličního názvosloví od začátku 20. století
                    do konce osmdesátých let je fascinujícím příběhem, který
                    zdaleka neodráží jen změny mapy, ale i hlubší společenské a
                    politické otřesy. S každou změnou režimu přicházela vlna
                    přejmenovávání – jednou kvůli odstranění habsburských jmen,
                    podruhé kvůli ideologickým čistkám nebo germanizaci, jindy
                    kvůli potřebě sjednotit názvy v nově připojených čtvrtích.
                    Článek ukazuje, jak se Praha snažila balancovat mezi
                    historií, aktuálními politickými tlaky a praktickou potřebou
                    orientace v dynamicky rostoucím městě.
                </p>

                <p class="font-bold">1900–1918</p>
                <p class="mb-5">
                    Spojením čtyř pražských měst v roce 1784 zdaleka neskončilo
                    úřední rozšiřování jejího území. Ještě v 19. století byl k
                    Praze připojen Josefov (1850), Vyšehrad (1883) a Holešovice
                    (1884) a v roce 1901 Libeň. S připojováním okolních obcí
                    byly připojovány ulice, které ale často nesly stejné názvy a
                    docházelo tak k duplicitám. Obecní rada si tento problém
                    uvědomovala a již roku 1903 schválila, aby při připojování
                    předměstí byly měněny názvy, a to v nově připojovaných
                    částech, nikoli původní pražské. Předměstí byla požádána,
                    aby při schvalování svých nových uličních názvů nepoužívala
                    názvů v Praze již existujících. Následně (roku 1904) bylo
                    rozhodnuto, že budou přejmenovány ulice v Libni, aby se
                    názvy neopakovaly, takže např. v roce 1905 byla přejmenována
                    libeňská
                    <span class="italic">Náprstkova</span>
                    na
                    <span class="italic">Braunerovu.</span>
                    Tím ovšem veškeré akce a tažení proti multiplicitním názvům
                    veřejných prostranství v Praze až do vzniku Velké Prahy
                    skončily.
                </p>

                <p class="font-bold">1918–1940</p>
                <p class="mb-5">
                    Rozpad rakousko-uherského mocnářství a vznik Československé
                    republiky 28. října 1918 navrátil Praze postavení hlavního
                    města samostatného státu. Obdobně jako Mariánský sloup,
                    socha Františka I. nebo pomník Radeckého zmizely již 12.
                    února 1919 usnesením pražského správního sboru z městských
                    prostranství všechny tabulky s názvy podle členů
                    habsbursko-lotrinské dynastie. Františkovo nábřeží se stalo

                    <span class="italic">Masarykovým </span>
                    a most císaře Františka
                    <span class="italic">mostem Legií.</span>
                    Ostatní dynastické názvy nahradila jména umělců (<span
                        class="italic"
                        >Smetanovo náměstí, Alšovo nábřeží</span
                    >), republikánské názvy (<span class="italic"
                        >náměstí Republiky, Revoluční třída</span
                    >), popřípadě názvy původní (<span class="italic"
                        >Malostranské náměstí, Letenské sady</span
                    >). Navíc se dosavadní Ovocná ulice stala ulicí
                    <span class="italic"> 28. října,</span>
                    ulice Na Rejdišti dostala jméno
                    <span class="italic"> U Akademického gymnázia </span>
                    a Ostrovní se stala
                    <span class="italic">Smetanovou.</span>
                    Snahám po dalších změnách v centru města zabránila
                    názvoslovná komise, která na sklonku roku 1918 vzešla z
                    ankety Klubu za starou Prahu. Byla složena ze spisovatelů,
                    umělců, historiků a znalců pražského místopisu. Jejím
                    tajemníkem se stal městský archivář Václav Vojtíšek. Podle
                    návrhu této komise schválila městská rada 9. ledna 1925
                    zásady pro pojmenování ulic. Impulzem k tomuto pokusu o
                    regulaci tvorby názvů veřejných prostranství bylo především
                    vytvoření tzv. Velké Prahy připojením 37 sousedních obcí k
                    dosavadním sedmi městským čtvrtím s účinností od 1. ledna
                    1922. Spojením takového počtu dosud samostatných měst,
                    městysů a vsí v jeden celek vznikl názvoslovný chaos.
                    Sjednocení uličních pojmenování podle určitých pravidel se
                    jevilo jako naprostá nezbytnost. K tomu vyvstala nutnost
                    pojmenovat desítky a časem již stovky ulic, které nově
                    vznikaly jako výsledek velké stavební konjunktury druhé
                    poloviny 20. let a v poněkud menší četnosti a zpomaleném
                    rytmu i v letech třicátých. Jen v letech 1925 a 1926 bylo
                    nově pojmenováno a přejmenováno přes 400 ulic. V naprosté
                    většině šlo o názvy nově vzniklých veřejných prostranství,
                    zatímco na odstraňování duplicit a sjednocování
                    celopražského systému zbylo jen málo času. Důsledně byly
                    opakující se názvy odstraněny pouze na Královských
                    Vinohradech (např.
                    <span class="italic">Komenského</span>
                    na
                    <span class="italic">Belgickou</span>), zčásti pak na
                    Žižkově a ve Vršovicích. A tak až do období po druhé světové
                    válce přežilo ve Velké Praze mimo jiné deset ulic
                    <span class="italic">Palackého,</span>
                    osm ulic
                    <span class="italic"> Husových,</span>
                    sedm
                    <span class="italic">Havlíčkových</span>
                    či pět
                    <span class="italic">Karlových.</span>
                </p>

                <p class="font-bold">1940–1945</p>
                <p class="mb-5">
                    Brzy po okupaci byl vznesen požadavek na zavedení německých
                    názvů do pražského uličního názvosloví. Již v dubnu 1940
                    vyšel tiskem německo-český

                    <span class="italic">
                        Úřední seznam ulic náměstí a sadů hlavního města Prahy,
                    </span>

                    který zaváděl důslednou dvoujazyčnost pražského uličního
                    názvosloví a současně byl pokusem o jeho zásadní reformu.
                    Byl zpracován s příslovečnou německou důkladností a vedle
                    německých ekvivalentů dosavadních českých názvů legislativně
                    zakotvoval též mnoho nových českých pojmenování. Odstraňoval
                    totiž nejen pro nacisty nepřijatelné názvy (<span
                        class="italic"
                        >Bachmačská, most Legií, Masarykovo nábřeží, Anglická
                    </span>
                    aj.), ale také většinu názvových duplicit a multiplicit.
                    Místo nich zaváděl pojmenování německého původu. Po velké
                    přejmenovávací akci z roku 1940 následovalo o rok později
                    pojmenování 183 nově vzniklých ulic v Praze XIV a XV. Bylo
                    provedeno neutrálními pomístními a zeměpisnými názvy, v
                    podstatě v duchu předválečných zásad. V dalším období
                    okupace došlo pak již pouze k několika příležitostným
                    přejmenováním.
                </p>

                <p class="font-bold">1945–1948</p>
                <p class="mb-5">
                    Když se v létě 1945 ujala funkce nová komise pro pojmenování
                    ulic Ústředního národního výboru, stály před ní tři hlavní
                    úkoly: /1/ pojmenovat 345 ulic v zástavbě, která vznikla
                    těsně před válkou a nedočkala se po dobu okupace uličních
                    názvů, /2/ přejmenovat asi 300 ulic, jejichž názvy se
                    opakovaly, /3/ odstranit názvy „nevhodné“. Proto byl roku
                    1946 schválen Řád pro jednotné označování veřejných
                    prostranství a komunikací a o číslování domů. Pravidla
                    vycházela z osvědčených zásad z roku 1925 a propracovávala
                    je do větších podrobností. Nově stanovila, že uliční názvy a
                    tabulky slouží především orientaci, že každé pojmenování smí
                    být užito v Praze pouze jednou, že jedna ulice nemá být
                    přerušena náměstím ani přecházet z jedné městské čtvrti do
                    druhé atd. Pojmenování podle živých osobností mělo být
                    užíváno jen výjimečně. Komise pro pojmenování ulic splnila
                    svůj úkol v několika etapách v průběhu let 1946 a 1947.
                    Označením dosud nepojmenovaných ulic, odstraněním opakování
                    stejných názvů a uplatněním přijatých pravidel, byť ne zcela
                    důsledným, nabylo názvosloví pražských veřejných
                    prostranství konečně povahy uceleného systému.
                </p>

                <p class="font-bold">1948–1960</p>
                <p class="mb-5">
                    Únorový převrat radikálně změnil rozložení sil i na pražské
                    radnici a dal pojmenovávání pražských veřejných prostranství
                    zcela nový „třídně ideologický“ směr. Veřejná prostranství
                    měla být pojmenována zejména po představitelích odboje a
                    domácího i mezinárodního dělnického a komunistického hnutí
                    (<span class="italic"
                        >Urxova, Peckova, Dimitrovovo nám.
                    </span>
                    aj.). Tyto tendence vyvrcholily roku 1952, kdy bylo hromadně
                    přejmenováno několik desítek ulic a náměstí, nesoucích dosud
                    názvy po osobnostech i událostech údajně reakčních a
                    protirevolučních. Výsledek popsaného vývoje názvosloví
                    pražských veřejných prostranství zhruba do poloviny
                    padesátých let 20. století zachytil jeho úplný soupis vydaný
                    tiskem, kolektivní dílo
                    <span class="italic"> Ulicemi města Prahy </span>
                    z roku 1958. V úvodu této knihy byla vyslovena optimistická
                    naděje, že provedenými přejmenováními se „do zmatků v
                    pražské orientaci zavedl řád a pražská pojmenování jsou
                    neměnná a trvalá“.
                </p>

                <p class="font-bold">1960–1989</p>
                <p class="mb-10 sm:mb-25">
                    Avšak již po dvou letech došlo v uličním názvosloví k dalším
                    změnám, které byly vyvolány územním rozšířením Prahy o nově
                    připojenou sousední obec Ruzyně, osadu Čimice a části
                    dalších deseti katastrálních území. Bylo nutno opět řešit
                    problematiku sjednocení uličního názvosloví a také označení
                    nových městských částí na uličních tabulkách. Stejný problém
                    se v daleko větším měřítku znovu opakoval v roce 1968, kdy
                    novým zákonem o hlavním městě Praze bylo k hlavnímu městu
                    připojeno dvacet sousedních obcí z okresů Praha-západ a
                    Praha-východ (Háje, Chodov, Kunratice, Libuš, Modřany,
                    Lahovice, Velká Chuchle, Řepy, Nebušice, Lysolaje, Suchdol;
                    Dolní Chabry, Ďáblice, Letňany, Čakovice, Kbely, Kyje, Horní
                    Měcholupy, Petrovice a Štěrboholy) a do třetice a
                    nejnaléhavěji vyvstal roku 1974, kdy připojením dalších
                    třiceti obcí nabyla Praha dnešního územního rozsahu. Téměř
                    každá z připojených obcí měla svou vlastní soustavu uličních
                    názvů a v každé se namnoze opakovala shodná pojmenování po
                    národních klasicích a významných historických i politických
                    osobnostech. Integrace těchto názvů do celopražské soustavy
                    byla velmi náročným problémem, jehož řešení ve všech třech
                    případech nebylo jednorázové, nýbrž se vždy protáhlo na
                    několik let.
                </p>

                <!-- next chapters -->
                <div class="mb-10 flex justify-between border-t-1 pt-5">
                    <p class="mb-5 w-1/2 border-r-1 font-bold">
                        <nuxt-link
                            to="/clanky/historie-ulic-do-roku-1900"
                            class="flex justify-start underline"
                        >
                            <img
                                src="../../public/img/left-arrow.png"
                                alt=""
                                class="order-1 mr-2 inline h-5 w-5"
                            />
                            <span class="order-2 mr-2"
                                >Historie ulic do roku 1900</span
                            >
                        </nuxt-link>
                    </p>
                    <p class="mb-5 w-1/2 text-right font-bold">
                        <nuxt-link
                            to="/clanky/historie-ulic-po-roce-1989"
                            class="flex justify-end underline"
                        >
                            <span class="order-1 ml-2"
                                >Historie ulic po roce 1989</span
                            >
                            <img
                                src="../../public/img/right-arrow.png"
                                alt=""
                                class="order-2 ml-2 inline h-5 w-5"
                            />
                        </nuxt-link>
                    </p>
                </div>
            </div>
            <div class="hidden md:col-span-1 md:block" />
        </div>

        <AppFooter />
    </div>
</template>

<script setup lang="ts"></script>

<style scoped></style>
